import { Field } from "@headlessui/react";
import type { Nullable } from "@msuite/utils/types";
import clsx from "clsx";
import type { FieldValues } from "react-hook-form";
import { Label } from "../label";
import type { ClassNameProps } from "../types";
import { getFormError } from "../utils/get-form-error";
import { InputWrapperContext } from "./context";
import { FormError, type FormErrorProps } from "./form-error";

/** Props Interface */
interface InputWrapperProps<T extends FieldValues> extends FormErrorProps<T>, ClassNameProps {
	children: React.ReactNode;
	label: Nullable<string>;
	helperText?: string | (() => React.ReactNode);
	isRequired: boolean;
	autoFocus?: boolean;
	isDisabled?: boolean;
	onLabelClick?: () => void;
	errorVariant?: "inline" | "tooltip";
}

export type ExtendedInputWrapperProps<T extends FieldValues> = Omit<
	InputWrapperProps<T>,
	"children" | "errors"
>;

export function InputWrapper<T extends FieldValues>({
	label,
	name,
	helperText,
	isRequired,
	children,
	errors,
	onLabelClick,
	className,
	errorVariant,
}: InputWrapperProps<T>) {
	const currentError = getFormError(errors, name);

	/** Render */
	return (
		<InputWrapperContext.Provider value={{ error: currentError }}>
			<Field
				className={clsx(
					"input-wrapper flex flex-col gap-2 w-full",
					"relative",
					currentError && "input-wrapper-error",
					className,
				)}
			>
				{label !== undefined ? (
					<div
						className="flex flex-row gap-1"
						onClick={onLabelClick}
					>
						<Label htmlFor={name}>{label}</Label>
						{isRequired ? <p className="text-rose-500 text-xs form-asterisk">*</p> : null}
					</div>
				) : null}
				{children}
				<FormError
					key={`form-error-${name}`}
					errors={errors}
					name={name}
					errorVariant={errorVariant}
				/>
				{helperText ? (
					typeof helperText === "string" ? (
						<p className="dark:opacity-50 text-primaryGray-400 text-xs form-helper-text">
							{helperText}
						</p>
					) : (
						helperText?.()
					)
				) : null}
			</Field>
		</InputWrapperContext.Provider>
	);
}
